const pollOptionsSchema = {
  type: "array",
  items: {
    type: "object",
    properties: {
      id: { type: "string" },
      title: { type: "string", minLength: 1 },
    },
    required: ["title", "id"],
  },
};

const votePollOptionsSchema = {
  oneOf: [
    {
      type: "array",
      items: {
        type: "object",
        properties: {
          id: { type: "string" },
        },
        required: ["id"],
      },
    },
    {
      type: "array",
      items: {
        type: "object",
        properties: {
          text: { type: "string" },
        },
        required: ["text"],
      },
    },
  ],
};

const status = { type: "string", enum: ["open", "closed"] };

const pollBaseSchema = {
  type: "object",
  properties: {
    title: { type: "string", minLength: 1 },
    isVisible: { type: "boolean" },
    status: { type: "string", enum: ["open", "closed"] },
    options: {
      oneOf: [{ type: "null" }, pollOptionsSchema],
    },
    workspaceId: { type: "number" },
    eventId: { type: "number" },
    description: { type: ["string", "null"] },
    sessionId: { type: "number" },
    areResultsVisible: { type: "boolean" },
    type: { type: "string", enum: ["singleChoice", "multipleChoice", "text"] },
  },
  required: ["title", "options", "workspaceId", "eventId", "sessionId"],
  if: {
    properties: { type: { const: "text" } },
  },
  then: {
    properties: {
      options: { type: "null" },
    },
  },
  else: {
    properties: {
      options: pollOptionsSchema,
    },
  },
};

export const createPollSchema = {
  oneOf: [
    pollBaseSchema,
    {
      type: "array",
      items: pollBaseSchema,
    },
  ],
};

export const answerPollSchema = {
  type: "object",
  properties: {
    answers: votePollOptionsSchema,
    type: { type: "string", enum: ["singleChoice", "multipleChoice", "text"] },
  },
  required: ["answers"],
};

export const updatePollStatusSchema = {
  type: "object",
  properties: {
    status,
  },
  required: ["status"],
};

export const updatePollSchema = {
  type: "object",
  properties: {
    pollId: { type: "number" },
    status,
    isVisible: { type: "boolean" },
    title: { type: "string", minLength: 1 },
    description: { type: ["string", "null"] },
    areResultsVisible: { type: "boolean" },
    sessionId: { type: "number" },
    options: {
      oneOf: [{ type: "null" }, pollOptionsSchema],
    },
    type: { type: "string", enum: ["singleChoice", "multipleChoice", "text"] },
  },
  required: ["title"],
};

export const editPollQuestionSchema = {
  type: "object",
  properties: {
    title: { type: "string", minLength: 1 },
    options: pollOptionsSchema,
  },
  required: ["title", "options"],
};

export const deletePollSchema = {
  body: {
    type: "object",
    properties: {
      id: { type: "string" },
    },
    required: ["id"],
  },
};

const web_optionsSchema = {
  type: "object",
  properties: {
    id: { type: "string" },
    title: { type: "string" },
  },
  required: ["id", "title"],
};

const web_choiceFollowUpValueSchema = {
  type: "object",
  properties: {
    id: { type: "string", minLength: 1 },
  },
  required: ["id"],
};

const web_basePollSchema = {
  type: "object",
  properties: {
    sessionId: { type: "string", minLength: 1 },
    workspaceId: { type: "number", minimum: 1 },
    eventId: { type: "number", minimum: 1 },
    title: { type: "string", minLength: 3 },
    description: { type: ["string", "null"] },
    followUpConditionType: {
      type: ["string", "null"],
      enum: ["isEqual", "isNotEqual", null],
    },
  },
  required: ["sessionId", "workspaceId", "eventId", "title"],
};

export const web_pollSchema = {
  allOf: [
    web_basePollSchema,
    {
      if: { properties: { type: { const: "text" } }, required: ["type"] },
      then: {
        properties: {
          type: { const: "text" },
          options: { type: "array", maxItems: 0 },
          followUpConditionValue: { type: ["string", "null"] },
        },
        required: ["type", "options"],
      },
      else: {
        properties: {
          type: { enum: ["singleChoice", "multipleChoice"] },
          options: {
            type: "array",
            items: web_optionsSchema,
            minItems: 2,
          },
          followUpConditionValue: {
            type: ["array", "null"],
            items: web_choiceFollowUpValueSchema,
          },
        },
        required: ["type", "options"],
      },
    },
  ],
};

const web_updateFields = {
  visibility: {
    type: "string",
    enum: ["visible", "hidden"],
  },
  status: {
    type: "string",
    enum: ["open", "closed"],
  },
  results: {
    type: "string",
    enum: ["shared", "hidden"],
  },
};

export const web_createPollsSchema = {
  type: "object",
  properties: {
    polls: {
      type: "array",
      items: web_pollSchema,
      minItems: 1,
    },
    common: {
      type: "object",
      properties: web_updateFields,
      required: Object.keys(web_updateFields),
    },
  },
  required: ["polls", "common"],
};

export const web_updatePollsSchema = {
  allOf: [
    {
      type: "object",
      properties: {
        ...web_updateFields,
        sessionId: { type: "string", minLength: 1 },
        workspaceId: { type: "number", minimum: 1 },
        eventId: { type: "number", minimum: 1 },
        title: { type: "string", minLength: 3 },
        description: { type: ["string", "null"] },
        followUpConditionType: {
          type: ["string", "null"],
          enum: ["isEqual", "isNotEqual", null],
        },
        followUpPollId: { type: ["number", "string", "null"] },
      },
      required: ["title", ...Object.keys(web_updateFields)],
    },
    {
      if: { properties: { type: { const: "text" } }, required: ["type"] },
      then: {
        properties: {
          type: { const: "text" },
          options: { type: "array", maxItems: 0 },
          followUpConditionValue: { type: ["string", "null"] },
        },
        required: ["type", "options"],
      },
      else: {
        properties: {
          type: { enum: ["singleChoice", "multipleChoice"] },
          options: {
            type: "array",
            items: web_optionsSchema,
            minItems: 2,
          },
          followUpConditionValue: {
            type: ["array", "null"],
            items: web_choiceFollowUpValueSchema,
          },
        },
        required: ["type", "options"],
      },
    },
  ],
};

export const shareableLinkSchema = {
  params: {
    type: "object",
    properties: {
      pollId: { type: "integer" },
    },
    required: ["pollId"],
  },
};

export const publicPollResultsSchema = {
  params: {
    type: "object",
    properties: {
      shareToken: { type: "string" },
    },
    required: ["shareToken"],
  },
};

export const reorderPollsSchema = {
  body: {
    type: "object",
    properties: {
      pollIds: {
        type: "array",
        items: { type: "number" },
        minItems: 1,
      },
      eventId: { type: "number" },
      sessionId: { type: "number" },
    },
    required: ["pollIds", "eventId", "sessionId"],
  },
};

export const toggleVisibilityOnMobileHomepageSchema = {
  body: {
    type: "object",
    properties: {
      sessionId: { type: "number" },
    },
    required: ["sessionId"],
  },
};
